import type { Maybe } from '@adornis/base/utilTypes.js';
import { Float } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';

export enum ConfirmationProgressStatus {
  PENDING = 'PENDING',
  STARTED = 'STARTED',
  RUNNING = 'RUNNING',
  DONE = 'DONE',
  FAILED = 'FAILED',
}

registerEnum({ enum: ConfirmationProgressStatus, name: 'ConfirmationProgressStatus' });

/**
 * The four phases of `runExamCreation` (exam-creation-agent.ts), in the order they always run — see that
 * file's own header comment. ONLY MEANINGFUL for `examCreationState`; unused (stays empty) for
 * `structureConfirmationState`/`splitConfirmationState`, which have no such phases.
 */
export enum ExamCreationPhase {
  PLAN = 'PLAN',
  MATERIAL = 'MATERIAL',
  SOURCE = 'SOURCE',
  REPAIR = 'REPAIR',
}

registerEnum({ enum: ExamCreationPhase, name: 'ExamCreationPhase' });

@Entity()
export class ConfirmationProgress extends AdornisEntity {
  static override _class = 'ConfirmationProgress';

  @Field(() => String)
  status!: ConfirmationProgressStatus;

  @Field(() => Boolean)
  confirmed!: boolean;

  @Field(() => String)
  messageId!: Maybe<string>;

  @Field(() => String)
  message!: Maybe<string>;

  @Field(() => Float, { default: v => v ?? 0 })
  total!: number;

  @Field(() => Float, { default: v => v ?? 0 })
  finished!: number;

  /**
   * ONLY MEANINGFUL for examCreationState: which of `ExamCreationPhase`'s four phases is currently
   * running. `total`/`finished` above are always relative to THIS phase's own unit of work — planned tasks
   * while SOURCE, materials while MATERIAL, repair rounds while REPAIR — and are reset every time this
   * changes (see `setExamCreationPhaseByExam`). Unused (stays empty) for
   * structureConfirmationState/splitConfirmationState.
   */
  @Field(() => String)
  phase!: Maybe<string>;

  /**
   * ONLY MEANINGFUL for examCreationState while phase === REPAIR. Flips true once the compiled PDF has
   * been persisted as a file (`PDFFile.createFromBuffer` in compile-exam.ts's `compileAndPersistExam`) —
   * the expensive part (the actual Typst compile) is done; only page-image extraction and the final
   * exam-doc write are left. Reset to false on every phase change, and unused (stays at its default) for
   * structureConfirmationState/splitConfirmationState.
   */
  @Field(() => Boolean)
  pdfPersisted = false;
}

type ConfirmationProgressKey = 'structureConfirmationState' | 'splitConfirmationState' | 'examCreationState';

async function updateConfirmationProgressByExam(
  examID: string,
  key: ConfirmationProgressKey,
  progress: Partial<ConfirmationProgress>,
): Promise<boolean> {
  if (!examID) throw new Error('examID required');

  const { Exam } = await import('./exam.js');
  const examCollection = await getCollection(Exam);
  const existingExam = await examCollection.findOne(
    { _id: examID },
    {
      projection: { [`examState.${key}`]: 1 },
    },
  );
  const existingState = (existingExam as any)?.examState?.[key] ?? {};

  const payload = new ConfirmationProgress({
    status: progress.status ?? existingState.status ?? ConfirmationProgressStatus.PENDING,
    confirmed: progress.confirmed ?? existingState.confirmed ?? false,
    messageId: progress.messageId ?? existingState.messageId ?? '',
    message: progress.message ?? existingState.message ?? '',
    total: progress.total ?? existingState.total ?? 0,
    finished: progress.finished ?? existingState.finished ?? 0,
    phase: progress.phase ?? existingState.phase ?? '',
    pdfPersisted: progress.pdfPersisted ?? existingState.pdfPersisted ?? false,
  }).toObject();

  await examCollection.updateOne(
    { _id: examID },
    {
      $set: {
        [`examState.${key}`]: payload,
      },
    },
  );

  return true;
}

export async function setStructureConfirmationProgressByExam(
  examID: string,
  progress: Partial<ConfirmationProgress>,
): Promise<boolean> {
  return updateConfirmationProgressByExam(examID, 'structureConfirmationState', progress);
}

export async function setSplitConfirmationProgressByExam(
  examID: string,
  progress: Partial<ConfirmationProgress>,
): Promise<boolean> {
  return updateConfirmationProgressByExam(examID, 'splitConfirmationState', progress);
}

/**
 * Progress for the exam-creator step: PENDING until a teacher first asks to create (or refine) an exam, so
 * that status is also what gates the step's visibility in the UI — see `mapConfirmationStatus` in
 * chat-input.tsx and `showExamCreatorStep` in chat.tsx.
 */
export async function setExamCreationProgressByExam(
  examID: string,
  progress: Partial<ConfirmationProgress>,
): Promise<boolean> {
  return updateConfirmationProgressByExam(examID, 'examCreationState', progress);
}

export async function incrementSplitConfirmationFinishedByExam(examID: string): Promise<void> {
  if (!examID) return;
  const { Exam } = await import('./exam.js');
  const examCollection = await getCollection(Exam);
  await examCollection.updateOne(
    { _id: examID },
    {
      $inc: { 'examState.splitConfirmationState.finished': 1 },
    },
  );
}

export async function setSplitConfirmationTotalByExam(examID: string, total: number): Promise<void> {
  if (!examID) return;
  const { Exam } = await import('./exam.js');
  const examCollection = await getCollection(Exam);
  await examCollection.updateOne(
    { _id: examID },
    {
      $set: {
        'examState.splitConfirmationState.total': total,
        'examState.splitConfirmationState.finished': 0,
      },
    },
  );
}

/**
 * PHASE TRANSITION for the exam-creator's progress ring. A direct, partial `$set` of ONLY
 * `phase`/`total`/`finished`/`pdfPersisted` — deliberately NOT routed through
 * `updateConfirmationProgressByExam` (the read-then-full-overwrite helper every other
 * `setExamCreation*Progress*` setter uses), so it can never race a still-in-flight
 * `setExamCreationFinishedByExam` write from the tail of the PREVIOUS phase and stamp the whole
 * subdocument back down to a stale `finished`. Touching only these four fields makes that impossible —
 * there is nothing to race.
 *
 * `total` is THIS phase's own denominator (planned tasks for SOURCE, materials for MATERIAL, repair
 * rounds for REPAIR — PLAN has none, so 0), and `finished`/`pdfPersisted` reset every time: a number left
 * over from the phase before would otherwise flash a wrong fraction until the new phase's own first update
 * lands.
 */
export async function setExamCreationPhaseByExam(examID: string, phase: ExamCreationPhase, total = 0): Promise<void> {
  if (!examID) return;
  const { Exam } = await import('./exam.js');
  const examCollection = await getCollection(Exam);
  await examCollection.updateOne(
    { _id: examID },
    {
      $set: {
        'examState.examCreationState.phase': phase,
        'examState.examCreationState.total': total,
        'examState.examCreationState.finished': 0,
        'examState.examCreationState.pdfPersisted': false,
      },
    },
  );
}

/**
 * An ABSOLUTE set rather than an increment, relative to whichever phase is currently running (see
 * `setExamCreationPhaseByExam`) — a fresh creation and a refinement both write the whole document as one
 * streamed call and count `#task(` declarations in the source accumulated so far, so the caller always
 * knows the true running total, and MATERIAL/REPAIR callers likewise always know their own true count. An
 * absolute set means re-sending the same number (or a repeated one after a stream hiccup) can never drift
 * the way an increment would.
 */
export async function setExamCreationFinishedByExam(examID: string, finished: number): Promise<void> {
  if (!examID) return;
  const { Exam } = await import('./exam.js');
  const examCollection = await getCollection(Exam);
  await examCollection.updateOne(
    { _id: examID },
    {
      $set: { 'examState.examCreationState.finished': finished },
    },
  );
}

/**
 * REPAIR-PHASE CHECKPOINT, called once, right after the compiled PDF is persisted as a file. See
 * `pdfPersisted` on ConfirmationProgress and `examCreatorPercent` in chat-input.tsx, which is the only
 * reader. Never rethrown — a progress write failing must not fail the creation it only describes, same
 * convention as `reportTasksWritten` in exam-creation-agent.ts.
 */
export async function setExamCreationPdfPersistedByExam(examID: string): Promise<void> {
  if (!examID) return;
  const { Exam } = await import('./exam.js');
  const examCollection = await getCollection(Exam);
  await examCollection.updateOne(
    { _id: examID },
    {
      $set: { 'examState.examCreationState.pdfPersisted': true },
    },
  );
}
